import React, { useEffect, useState } from "react";
import ProductCard from "./ProductCard";
import { useCarrito } from "../context/CarritoContext";
import toast from "react-hot-toast";
import { auth } from "../utils/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

export default function ProductGrid({ products, paginaActual, setSearchParams, productosRef }) {
  const [currentPage, setCurrentPage] = useState(paginaActual || 1);
  const [productsPerPage, setProductsPerPage] = useState(8);
  const [isAdmin, setIsAdmin] = useState(false);
  const { agregarAlCarrito } = useCarrito();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === "admin@empresa.com") {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setCurrentPage(paginaActual || 1);
  }, [paginaActual]);

  const handleAddToCart = (product) => {
    if (!product.stock || product.stock <= 0) {
      toast.error(`${product.nombre} está agotado`);
      return;
    }

    agregarAlCarrito({
      ...product,
      cantidad: 1,
    });

    toast.success(`${product.nombre} agregado al carrito`);
  };

  // Paginación
  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;
  const currentProducts = products.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("page", page);
        return newParams;
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  function getPaginasVisibles(paginaActual, totalPaginas) {
    const paginas = [];
  
    if (totalPaginas <= 10) {
      for (let i = 1; i <= totalPaginas; i++) paginas.push(i);
    } else {
      if (paginaActual <= 4) {
        paginas.push(1, 2, 3, 4, 5, "...", totalPaginas);
      } else if (paginaActual >= totalPaginas - 3) {
        paginas.push(1, "...", totalPaginas - 4, totalPaginas - 3, totalPaginas - 2, totalPaginas - 1, totalPaginas);
      } else {
        paginas.push(1, "...", paginaActual - 1, paginaActual, paginaActual + 1, "...", totalPaginas);
      }
    }
  
    return paginas;
  }

  return (
    <div ref={productosRef} className="product-grid-container scroll-mt-28">
      {/* Control superior con selector y contador */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="text-sm text-gray-500">
          Mostrando {indexOfFirst + 1}-{Math.min(indexOfLast, products.length)} de {products.length} productos
        </div>
        
        <div className="flex items-center space-x-2 w-full sm:w-auto">
          <label htmlFor="productsPerPage" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Productos por página:
          </label>
          <div className="relative flex-grow sm:flex-grow-0">
            <select
              id="productsPerPage"
              value={productsPerPage}
              onChange={(e) => {
                setProductsPerPage(Number(e.target.value));
                goToPage(1); // Aquí se mantiene la lógica de regresar a página 1
              }}
              className="appearance-none w-full bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value={4}>4</option>
              <option value={8}>8</option>
              <option value={12}>12</option>
              <option value={16}>16</option>
              <option value={24}>24</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de productos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {currentProducts.map((product, index) => (
          <ProductCard
            key={`${product.id}-${index}`}
            product={product}
            isAdmin={isAdmin}
            onAddToCart={() => handleAddToCart(product)}
          />
        ))}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center flex-wrap gap-2 px-4">
          {/* Botón primera página */}
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className={`
              w-10 h-10 flex items-center justify-center rounded-full text-lg font-medium transition-all duration-200
              ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-sky-700 hover:bg-sky-50 hover:text-sky-900 ring-1 ring-gray-200 hover:ring-sky-300 shadow-sm hover:shadow-md"
              }
            `}
            aria-label="Primera página"
          >
            ‹‹
          </button>
          
          {/* Botón anterior */}
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`
              w-10 h-10 flex items-center justify-center rounded-full text-lg font-medium transition-all duration-200
              ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-sky-700 hover:bg-sky-50 hover:text-sky-900 ring-1 ring-gray-200 hover:ring-sky-300 shadow-sm hover:shadow-md"
              }
            `}
            aria-label="Página anterior"
          >
            ‹
          </button>
          
          {/* Números de página */}
          {getPaginasVisibles(currentPage, totalPages).map((num, index) =>
            num === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-1 text-gray-400 text-base select-none"
              >
                ...
              </span>
            ) : (
              <button
                key={num}
                onClick={() => goToPage(num)}
                className={`
                  w-10 h-10 flex items-center justify-center rounded-full text-base font-medium transition-all duration-200
                  ${currentPage === num
                    ? "bg-gradient-to-br from-cyan-500 to-sky-600 text-white shadow-md"
                    : "bg-white text-sky-700 ring-1 ring-gray-200 hover:bg-sky-50 hover:text-sky-900 hover:ring-sky-300 shadow-sm hover:shadow-md"
                  }
                `}
                aria-label={`Ir a página ${num}`}
              >
                {num}
              </button>
            )
          )}
          
          {/* Botón siguiente */}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`
              w-10 h-10 flex items-center justify-center rounded-full text-lg font-medium transition-all duration-200
              ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-sky-700 hover:bg-sky-50 hover:text-sky-900 ring-1 ring-gray-200 hover:ring-sky-300 shadow-sm hover:shadow-md"
              }
            `}
            aria-label="Página siguiente"
          >
            ›
          </button>
          
          {/* Botón última página */}
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className={`
              w-10 h-10 flex items-center justify-center rounded-full text-lg font-medium transition-all duration-200
              ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-sky-700 hover:bg-sky-50 hover:text-sky-900 ring-1 ring-gray-200 hover:ring-sky-300 shadow-sm hover:shadow-md"
              }
            `}
            aria-label="Última página"
          >
            ››
          </button>
        </div>
      )}
    </div>
  );
}
